import { APP_VERSION, BUILD_TIME } from "@/utils/buildInfo";
import {
  APP_ID,
  DIAG_ENABLED,
  DIAGNOSTICS_STATE_URL,
  QR_BUDGET_BYTES,
  QR_CHUNK_BYTES,
  QR_MAX_PARTS,
  SERVICE_BOOT_REFETCH_MS,
  SERVICE_BOOT_URL,
} from "./config";
import { Collector, type LiveEvent, type MetricSummary } from "./collector";
import { evaluateHealth } from "./health";
import { loadState, PersistScheduler, stateFreshness, type PersistedState } from "./persist";
import { createStateMirror } from "./remote";
import { buildSnapshot, type CapturedError, type SnapshotSD3 } from "./snapshot";
import { sectionSizes, type SectionSizes } from "./codec";
import { trimToBudget, type TrimReport } from "./trim";
import { byteLengthOf, frameSnapshot } from "./framing";

// 診断レイヤのファサード。アプリが触るのはこのファイルのみ。
// 収集処理が例外を投げても本体をクラッシュさせない (キオスク=無人・常時稼働)。

export type { SnapshotSD3, SectionSizes, TrimReport, CapturedError, LiveEvent, MetricSummary };
export { expandDay, expandError, SCHEMA, DAY_COLS } from "./snapshot";
export type { ExpandedDay, DayTuple, ErrTuple } from "./snapshot";

const startedAt = Date.now();
let bootIso = new Date(startedAt).toISOString();
let svcBootIso: string | undefined;
let scheduler: PersistScheduler | null = null;
const mirror = DIAGNOSTICS_STATE_URL ? createStateMirror(DIAGNOSTICS_STATE_URL) : null;

const collector = new Collector(() => {
  scheduler?.schedule();
  notify();
});

type Subscriber = () => void;
const subscribers = new Set<Subscriber>();
const notify = (): void => {
  subscribers.forEach((fn) => {
    try {
      fn();
    } catch {
      // noop
    }
  });
};

const guard = (fn: () => void): void => {
  if (!DIAG_ENABLED) return;
  try {
    fn();
  } catch {
    // 収集は本体アプリより優先度が低い。失敗は握りつぶす。
  }
};

const persistState = (): PersistedState => ({
  li: collector.lastInteractionAt,
  ss: collector.sessionStartAt,
  days: collector.store.serialize(),
});

// --- グローバル捕捉 (window error / unhandledrejection / console patch) ---
// window エラーは実エラーとして e 区画+日次カウントへ。console.warn/error は
// パネル用ライブイベントのみ (アプリが自前で出すログをエラー統計に混ぜない)。

let captureInstalled = false;
const installGlobalCapture = (): void => {
  if (captureInstalled || typeof window === "undefined") return;
  captureInstalled = true;

  window.addEventListener("error", (event) => {
    guard(() => {
      if (event.error) collector.recordError(event.error);
      else collector.recordError(new Error(`${event.message} @ ${event.filename}:${event.lineno}`));
    });
  });
  window.addEventListener("unhandledrejection", (event) => {
    guard(() => collector.recordError(event.reason));
  });

  const originalError = console.error.bind(console);
  const originalWarn = console.warn.bind(console);
  console.error = (...args: unknown[]) => {
    guard(() => collector.recordEvent("console_error", args.map(String).join(" ").slice(0, 120)));
    originalError(...args);
  };
  console.warn = (...args: unknown[]) => {
    guard(() => collector.recordEvent("console_warn", args.map(String).join(" ").slice(0, 120)));
    originalWarn(...args);
  };
};

// --- 実ユーザー入力の捕捉。capture+passive で本体UIに干渉しない ---

let trackingInstalled = false;
const installInteractionTracking = (): void => {
  if (trackingInstalled || typeof window === "undefined") return;
  trackingInstalled = true;

  window.addEventListener(
    "pointerdown",
    (e) => {
      guard(() => {
        const el = e.target instanceof Element ? e.target : null;
        const screen = el?.closest("[data-scr]")?.getAttribute("data-scr") ?? "other";
        collector.tap(screen);
      });
    },
    { capture: true, passive: true },
  );
  window.addEventListener("keydown", () => guard(() => collector.interaction()), { capture: true });
};

// Windowsサービスが書き出す起動時刻 (無ければ省略。実機依存を失敗許容にする)。
let svcBootFetchedAt = 0;
const fetchServiceBoot = (): void => {
  if (typeof window === "undefined") return;
  svcBootFetchedAt = Date.now();
  try {
    fetch(SERVICE_BOOT_URL, { cache: "no-store" })
      .then((res) => (res.ok ? res.json() : null))
      .then((data: unknown) => {
        if (data && typeof data === "object" && "bootAt" in data) {
          const v = (data as { bootAt: unknown }).bootAt;
          if (typeof v === "string") svcBootIso = v;
        }
      })
      .catch(() => {
        // ファイルが無い環境 (開発時等) は正常系。
      });
  } catch {
    // fetch 自体が無い環境 (テスト等) も正常系。
  }
};

// --- スナップショット生成 ---

const getSnapshot = (): SnapshotSD3 => {
  const now = Date.now();
  // サービスがページ表示中に再起動 (クラッシュ復旧等) した場合も次回スナップショットで
  // 反映されるよう再取得する。パネルは1秒毎に呼ぶためスロットルする。非同期なので
  // 今回の返り値には載らず、反映は次回以降。
  if (now - svcBootFetchedAt > SERVICE_BOOT_REFETCH_MS) fetchServiceBoot();
  return buildSnapshot({
    nowIso: new Date(now).toISOString(),
    id: {
      app: APP_ID,
      hash: APP_VERSION,
      built: BUILD_TIME,
      boot: bootIso,
      ...(svcBootIso ? { svcBoot: svcBootIso } : {}),
    },
    health: evaluateHealth(now, startedAt, collector.lastInteractionAt),
    days: collector.days(),
    errors: collector.capturedErrors(),
  });
};

export interface QRPartsResult {
  parts: string[]; // 各QRに描画するテキスト (1件なら生JSON、2件以上なら SDQ1 フレーム)
  partCount: number;
  totalBytes: number;
  sizes: SectionSizes; // 間引き前の区画別サイズ (RQ1 図表素材)
  report: TrimReport;
}

// 分割が第一手段、間引きは最終手段。予算 = chunkBytes × maxParts。
const getQRParts = (chunkBytes = QR_CHUNK_BYTES, maxParts = QR_MAX_PARTS): QRPartsResult => {
  const snap = getSnapshot();
  const sizes = sectionSizes(snap);
  const { json, byteSize, report } = trimToBudget(snap, chunkBytes * maxParts);
  const parts = frameSnapshot(json, { chunkBytes, maxParts });
  return { parts, partCount: parts.length, totalBytes: byteSize, sizes, report };
};

// サーバーミラーからの復元。ブラウザ側ストレージが消えた環境 (InPrivate・プロファイル
// 再作成等) でも直近 PUT 済みの状態から継続する。全文書 newest-wins (単一ライター前提
// なのでマージ不要)。pull 完了 (≤3秒) 前のタップは差し替えで失われ得るが、無人キオスク
// では許容する。
const pullRemoteState = (local: PersistedState | null): void => {
  if (!mirror) return;
  mirror
    .pull()
    .then((remote) => {
      guard(() => {
        if (!remote) return;
        const localFreshness = Math.max(
          local ? stateFreshness(local) : 0,
          collector.lastInteractionAt ?? 0,
        );
        if (stateFreshness(remote) <= localFreshness) return;
        collector.restore(remote.li, remote.ss, remote.days);
        scheduler?.flush(); // 採用した状態を即 localStorage にも書き戻す
        notify();
      });
    })
    .catch(() => {
      // ミラーが無い/届かない環境は正常系
    });
};

const initialize = (): void => {
  if (!DIAG_ENABLED) return;
  guard(() => {
    bootIso = new Date(startedAt).toISOString();
    const persisted = loadState();
    if (persisted) collector.restore(persisted.li, persisted.ss, persisted.days);
    scheduler = new PersistScheduler(persistState, undefined, (state) => mirror?.push(state));
    installGlobalCapture();
    installInteractionTracking();
    fetchServiceBoot();
    pullRemoteState(persisted);
  });
};

export const diagnostics = {
  initialize,
  // 記録 (すべて防御的)
  recordEvent: (type: string, target?: string): void =>
    guard(() => collector.recordEvent(type, target)),
  recordError: (err: unknown, context?: string): void =>
    guard(() => collector.recordError(err, context)),
  recordSearch: (facilityId: string | null | undefined): void =>
    guard(() => collector.recordSearch(facilityId)),
  recordMetric: (name: string, valueMs: number): void =>
    guard(() => collector.recordMetric(name, valueMs)),
  measure<T>(name: string, fn: () => T | Promise<T>): T | Promise<T> {
    if (!DIAG_ENABLED) return fn();
    const start = performance.now();
    const finish = () => diagnostics.recordMetric(name, performance.now() - start);
    try {
      const result = fn();
      if (result instanceof Promise) {
        return result.finally(finish);
      }
      finish();
      return result;
    } catch (err) {
      finish();
      throw err;
    }
  },
  // 読み出し (パネル/Error Boundary 用)
  getSnapshot,
  getSectionSizes: (): SectionSizes => sectionSizes(getSnapshot()),
  getQRParts,
  getLiveEvents: (): LiveEvent[] => collector.getLiveEvents(),
  getLiveErrors: (): LiveEvent[] => collector.getLiveErrors(),
  metricSummary: (name: string): MetricSummary | null => collector.metricSummary(name),
  isEnabled: (): boolean => DIAG_ENABLED,
  // 回収済みデータの破棄 (保守パネルの reset)
  reset: (): void =>
    guard(() => {
      collector.reset();
      scheduler?.flush();
    }),
  subscribe: (fn: Subscriber): (() => void) => {
    subscribers.add(fn);
    return () => {
      subscribers.delete(fn);
    };
  },
};

export const DIAG_QR_BUDGET = {
  chunkBytes: QR_CHUNK_BYTES,
  maxParts: QR_MAX_PARTS,
  budgetBytes: QR_BUDGET_BYTES,
};
export { byteLengthOf };
